
import { Component, Vue, namespace } from 'nuxt-property-decorator';
import {
  UserProfileDto,
  FormEntryDto,
  FormDto,
  getFirstName,
  getLastName,
  getSubmissionTitle,
  isSuccess,
  PageDto,
  EventDto,
  FormType,
  PageScope,
  PageType,
  FormFieldType,
  SubmissionScope,
  SubmissionDecisionEnum,
} from 'fourwaves-shared';
import { ParticipantHeader, ParticipantBody, NoResults, PresentationElement } from 'fourwaves-shared/components';
import { NuxtError } from '@nuxt/types';

const EventsModule = namespace('events');

@Component({
  components: {
    ParticipantHeader,
    ParticipantBody,
    NoResults,
  },
  validate({ params, store }) {
    // identifier validation
    if (!/^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/.test(params.id)) {
      return false;
    }
    const page = store.state.events.pages.find(page => page.type === PageType.Participants);
    if (page && page.scope === PageScope.Participants) {
      if (!store.$auth.loggedIn) {
        const error: NuxtError = new Error();
        error.statusCode = 401;
        throw error;
      } else if (!store.getters['events/isAllowedToVirtualCall']) {
        const error: NuxtError = new Error();
        error.statusCode = 403;
        throw error;
      }
    }
    return !!page;
  },
})
export default class ParticipantPage extends Vue {
  @EventsModule.Getter currentEvent!: EventDto;
  @EventsModule.Getter isParticipant!: boolean;
  @EventsModule.Getter isOrganizer!: boolean;
  @EventsModule.State pages!: PageDto[];

  form: FormDto | null = null;
  submissionForm: FormDto | null = null;
  formEntry: FormEntryDto | null = null;
  user: UserProfileDto | null = null;
  presentations: PresentationElement[] = [];
  firstLoadCompleted = false;

  pageType: PageType = PageType.Participants;

  get hasInformation(): boolean {
    const page = this.pages.find(x => x.type === this.pageType);
    let fields = page ? page.formFields : [];

    fields = fields
      .filter(x => this.formEntry?.formEntryValues.some(fev => fev.formFieldId === x.id))
      .filter(x => this.form?.formSections.flatMap(fs => fs.formFields).some(fev => fev.id === x.id));

    return Boolean(this.user?.bio) || this.presentations.length > 0 || fields.length > 0;
  }

  async created() {
    this.form = this.currentEvent.forms.find(x => x.type === FormType.Registration)!;

    // fetch participant form entry, if not found 404 redirect
    this.formEntry = await this.$api.getFormEntry(this.$route.params.id);
    if (!this.formEntry)
      return this.$router.replace(this.localePath({ name: 'slug-errors-code', params: { code: '404' } }));

    // fetch user profile
    this.user = await this.$api.getUserById(this.formEntry.userId);

    // fetch form
    this.submissionForm = this.currentEvent.forms.find(x => x.type === FormType.Submission)!;

    const abstractsPage = this.pages.find(({ type }) => type === PageType.Abstracts);

    if (abstractsPage) {
      const paginatedPayload = await this.$api.getFormEntries({
        formId: this.submissionForm.id,
        authorUserId: this.formEntry.userId,
      });

      if (paginatedPayload && paginatedPayload.items) {
        if (abstractsPage.submissionScope === SubmissionScope.Accepted) {
          paginatedPayload.items = paginatedPayload.items.filter(({ submissionDecisions }) =>
            submissionDecisions.some(({ decision }) => decision === SubmissionDecisionEnum.Accepted),
          );
        }
        this.presentations = paginatedPayload.items.map(this.buildPresentationElement);
      }
    }

    if (!this.firstLoadCompleted) this.firstLoadCompleted = true;
  }

  public buildPresentationElement(presentation: FormEntryDto): PresentationElement {
    return {
      ...presentation,
      thumbnail: presentation.thumbnail ? `${presentation.thumbnail}?fit=max&w=500&h=500` : undefined,
      submitterFirstName: getFirstName(presentation, this.submissionForm!),
      submitterLastName: getLastName(presentation, this.submissionForm!),
      title: getSubmissionTitle(presentation, this.submissionForm!),
      connectionCount: presentation.videoParticipantCount,
      hasVideo: presentation.formEntryValues.some(({ fieldType }) => fieldType === FormFieldType.Video),
    };
  }

  public async toggleBookmark() {
    if (!this.formEntry || !this.$auth.loggedIn) return;

    const { id, isBookmarkedByCurrentUser } = this.formEntry;

    const updateResult = isBookmarkedByCurrentUser
      ? await this.$api.unbookmarkFormEntry(id)
      : await this.$api.bookmarkFormEntry(id);

    if (isSuccess(updateResult)) this.formEntry.isBookmarkedByCurrentUser = !isBookmarkedByCurrentUser;
  }

  public async togglePresentationBookmark({ id, isBookmarkedByCurrentUser }: PresentationElement) {
    const presentation = this.presentations.find(p => p.id === id);
    if (!presentation || !this.$auth.loggedIn) return;

    const updateResult = isBookmarkedByCurrentUser
      ? await this.$api.unbookmarkFormEntry(id)
      : await this.$api.bookmarkFormEntry(id);

    if (isSuccess(updateResult)) presentation.isBookmarkedByCurrentUser = !isBookmarkedByCurrentUser;
  }

  public async togglePresentationUpvote({ id, isUpvotedByCurrentUser, upvoteCount }: PresentationElement) {
    const canUserVote = this.isParticipant || this.isOrganizer;
    const presentation = this.presentations.find(p => p.id === id);
    if (!presentation || !canUserVote) return;

    const updateResult = isUpvotedByCurrentUser
      ? await this.$api.deleteFormEntryUpvote(id)
      : await this.$api.upvoteFormEntry(id);

    if (isSuccess(updateResult)) {
      presentation.upvoteCount = isUpvotedByCurrentUser ? upvoteCount - 1 : upvoteCount + 1;
      presentation.isUpvotedByCurrentUser = !isUpvotedByCurrentUser;
    }
  }
}
